import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BuyerReportViewer } from '../components/BuyerReportViewer';
import TermsAcceptanceModal from '../components/TermsAcceptanceModal';
import { useTermsAcceptance } from '../hooks/useTermsAcceptance';
import Button from '../components/ui/Button';
import { api } from '../utils/api';
import { trackEvent } from '../utils/analytics';

export default function PlanningReport() {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const { showModal: showTermsModal, checkAcceptance, handleAccept, handleDecline, isAccepting, error: termsError } = useTermsAcceptance();

  if (!reportId) {
    return (
      <main className="bg-white">
        <div className="container-main section-spacing">
          <div className="error" role="alert">
            <h2>Report not found</h2>
            <p>The report ID is missing from the URL.</p>
            <Button 
              onClick={() => navigate('/planning-check')}
              className="mt-4"
            >
              Back to Planning Check
            </Button>
          </div>
        </div>
      </main>
    );
  }

  // Kick off Stripe Checkout: create a Checkout Session server-side and
  // hand the browser off to Stripe's hosted page. The order-confirmation
  // page is reached via Stripe's success_url redirect — we never navigate
  // there from here. planning_payment_completed is now fired from the
  // webhook-driven fulfilment path, not from this click handler.
  const redirectToCheckout = async () => {
    if (isRedirecting) return; // Guard against double-invocation while in flight.
    setIsRedirecting(true);
    setPaymentError(null);
    try {
      const session = await api.createCheckoutSession({
        productType: 'planning_report',
        productId: reportId,
      });
      // Do NOT clear isRedirecting on success — page is about to unload.
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      window.location.assign(session.url);
    } catch (error) {
      trackEvent({
        name: 'planning_payment_failed',
        properties: { reason: error instanceof Error ? error.message : 'unknown' },
      });
      setPaymentError(error instanceof Error ? error.message : 'Failed to start payment');
      setIsRedirecting(false);
    }
  };

  const handlePaymentRequired = async () => {
    const accepted = await checkAcceptance();
    if (accepted) {
      trackEvent({ name: 'planning_payment_started' });
      await redirectToCheckout();
    }
  };

  const handleTermsAccepted = async () => {
    await handleAccept();
    trackEvent({ name: 'planning_payment_started' });
    await redirectToCheckout();
  };

  return (
    <>
      <Helmet>
        <title>Planning Report | UKPropertyChecks</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <main className="bg-white">
        <div className="container-main section-spacing">
          <div className="mb-6">
            <Button 
              variant="ghost"
              onClick={() => navigate('/planning-check')}
            >
              ← Back to Planning Check
            </Button>
          </div>

          {paymentError && (
            <div
              role="alert"
              className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm"
            >
              <p className="font-semibold">Payment could not be started</p>
              <p className="mt-1">{paymentError}</p>
            </div>
          )}

          <BuyerReportViewer 
            reportId={reportId} 
            onPaymentRequired={handlePaymentRequired}
            isPaymentPending={isRedirecting}
          />
        </div>
      </main>

      {showTermsModal && (
        <TermsAcceptanceModal
          onAccept={handleTermsAccepted}
          onDecline={handleDecline}
          isAccepting={isAccepting}
          error={termsError}
        />
      )}
    </>
  );
}
